import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useContext, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import AvatarGroup from '@mui/material/AvatarGroup';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import TextField from '@mui/material/TextField';
import SendIcon from '@mui/icons-material/Send';
import InputAdornment from '@mui/material/InputAdornment';
import { useApolloClient } from '@apollo/client';
import useNotification from '../../components/_hook/useNotification';
import { useChatroomQuery } from '../../../graphql/query/chatroom/useChatroomQuery';
import { IMessageSchema } from '../../../graphql/type/message';
import SchemaCompanyLogoAvatar from '../../components/_schema/SchemaCompanyLogoAvatar';
import SchemaUserAvatar from '../../components/_schema/SchemaUserAvatar';
import ButtonSubmit from '../../components/_button/ButtonSubmit';
import { useChatroomMessageCreatedSubscription } from '../../../graphql/subscription/chatroom/chatroomMessageCreatedSubscription';
import { messageQuery } from '../../../graphql/query/message/useMessageQuery';
import NotFound from '../../components/_error/NotFound';
import Message from './Message';
import { useCreateChatroomMutation } from '../../../graphql/mutation/chatroom/createChatroomMutation';
import Context from '../../context';
import { useCreateMessageMutation } from '../../../graphql/mutation/message/createMessageMutation';
import { useUpdateChatroomLastSeenMutation } from '../../../graphql/mutation/chatroom/updateChatroomLastSeenMutation';
import { IPagination } from '../../../graphql/query/_module/baseQueryType';

interface IProps {
  chatroomId?: string | null;
  onClose?: () => void;
  receiver?: {
    userId?: string;
    companyId?: string;
  };
  defaultMessage?: string;
  onSendSuccessful?: () => void;
  onChatroomCreated?: (chatroomId: string) => void;
  defaultMessageTags?: string[];
  isModal?: boolean;
}

const useStyles = makeStyles<Theme, IProps>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    width: '100%',
  },
  messageContainer: {
    padding: theme.spacing(2),
    maxHeight: ({ isModal }) => (isModal ? '500px' : 'unset'),
    minHeight: ({ isModal }) => (isModal ? '500px' : 'unset'),
    flexFlow: 'column-reverse',
    flexDirection: 'column-reverse',
    overflow: 'auto',
    margin: 0,
    height: ({ isModal }) => (isModal ? '500px' : 'calc(100% - 147px)'),
    backgroundColor: theme.palette.mode === 'light' ? '#e5e5e5' : '#151515',
  },
  avatarContainer: {
    height: 'fit-content',
    padding: theme.spacing(2),
    zIndex: 1,
    margin: 0,
    backgroundColor:
      theme.palette.mode === 'light' ? theme.palette.primary.main : '#424141',
    color: '#fff',
  },
  textfieldMessage: {
    '&>div': {
      padding: '24px 10px',
      backgroundColor:
        theme.palette.mode === 'light' ? '#fff !important' : '#444 !important',
      '&:hover': {
        backgroundColor:
          theme.palette.mode === 'light'
            ? '#fff !important'
            : '#444 !important',
      },
    },
  },
  root: {
    height: '100%',
  },
  inputContainer: {
    height: 'fit-content',
  },
}));

export default function Chatroom(props: IProps) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const {
    chatroomId: propChatroomId,
    onClose,
    receiver,
    defaultMessage,
    onSendSuccessful,
    defaultMessageTags,
    onChatroomCreated,
    isModal,
  } = props;
  const classes = useStyles(props);
  const { notification } = useNotification();
  const [messages, setMessages] = useState<IMessageSchema[]>([]);
  const [
    messagePagination,
    setMessagePagination,
  ] = useState<IPagination | null>(null);
  const {
    company: contextCompany,
    user: contextUser,
    isSuperAdmin,
  } = useContext(Context);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(30);
  const [chatroomId, setChatroomId] = useState<string>(propChatroomId || '');
  const [message, setMessage] = useState<string>(defaultMessage || '');
  const [messageTags, setMessageTags] = useState<string[]>(
    defaultMessageTags || []
  );
  useEffect(() => {
    const messageContainer = document.getElementById('message-container');

    if (messageContainer) {
      messageContainer.scrollTo({
        top: messageContainer.scrollHeight,
      });
    }
  }, []);

  useEffect(() => {
    setChatroomId(propChatroomId || '');
  }, [propChatroomId]);

  const messageFragment = `_id
            room_id
            text
            tags
            created {
             time 
             user
             userData {
               _id 
               profile {
                name {
                  firstname
                  lastname
                }
                media {
                  images {
                    avatar {
                      status
                      filename
                      filename_small
                      
                      filenameUrl
                      filenameSmallUrl
                    }
                  }
                }
               }
             }
           } `;

  const { data, loading, refetch } = useChatroomQuery(
    `
			_id
            users {
              id
              userData {
                 _id 
                 profile {
                  name {
                    firstname
                    lastname
                  }
                  media {
                    images {
                      avatar {
                        status
                        filename
                        filename_small
                        
                        filenameUrl
                        filenameSmallUrl
                      }
                    }
                  }
                 }
              }
              from
              companyData {
                _id
                name {
                    la
                    text
                }
                origin
                slug
                media {
                  images {
                    logo {
                      status
                      filename
                      filename_large
                      
                      filenameUrl
                      filenameLargeUrl
                    }
                  }
                }
              }
            }
            seen {
              id
              from
              lastseen
            }
            lastmessage {
              text
              messageData {
                ${messageFragment}
              }
              created {
                time
              }
            }
		`,
    {
      variables: {
        filter: {
          _id: chatroomId,
          companyId: contextCompany._id,
          userId: contextUser._id,
        },
      },
      skip: !chatroomId,
    }
  );

  const [
    updateChatroomLastSeenMutation,
    { loading: isUpdatingChatroomLastSeen },
  ] = useUpdateChatroomLastSeenMutation(
    `updatedChatroom {
				_id
				seen {
                  id
                  from
                  lastseen
                }
                lastmessage {
                  text
                  messageData {
                    ${messageFragment}
                  }
                  created {
                    time
                  }
                }
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [
    createChatroomMutation,
    { loading: isCreatingChatroom },
  ] = useCreateChatroomMutation(
    `createdChatroom {
				_id
			}`,
    {
      onCompleted: (tempData) => {
        if (
          tempData &&
          tempData.createChatroom &&
          tempData.createChatroom.createdChatroom &&
          tempData.createChatroom.createdChatroom._id
        ) {
          setChatroomId(tempData.createChatroom.createdChatroom._id);
          if (onChatroomCreated) {
            onChatroomCreated(tempData.createChatroom.createdChatroom._id);
          }
          refetch();
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const chatroom = chatroomId && data ? data.chatrooms.items[0] : null;

  useEffect(() => {
    if (
      !chatroomId &&
      !chatroom &&
      !loading &&
      !isCreatingChatroom &&
      receiver
    ) {
      if (receiver.userId && contextUser._id === receiver.userId) {
        notification.error(t('are_you_messaging_to_yourself'));
        if (onClose) {
          onClose();
        }
      } else {
        createChatroomMutation({
          variables: {
            fromCompanyId: contextCompany._id,
            toCompanyId: receiver.companyId,
            toUserId: receiver.userId,
          },
        });
      }
    }
  }, [chatroomId, JSON.stringify(chatroom), JSON.stringify(receiver)]);

  const [createMessageMutation] = useCreateMessageMutation(
    `createdMessage {
				 ${messageFragment}
			}`,
    {
      onCompleted: (tempData) => {
        if (
          tempData &&
          tempData.createMessage &&
          tempData.createMessage.createdMessage
        ) {
          // setMessages([
          //   ...[tempData.createMessage.createdMessage as IMessageSchema],
          //   ...messages,
          // ]);
          setMessage('');
          setMessageTags([]);
          if (onSendSuccessful) {
            onSendSuccessful();
          }
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  function updateChatroomLastSeen() {
    if (chatroomId) {
      updateChatroomLastSeenMutation({
        variables: {
          _id: chatroomId,
          userId: contextUser._id,
          companyId: contextCompany._id,
        },
      });
    }
  }

  useEffect(() => {
    updateChatroomLastSeen();
  }, [chatroomId]);

  useChatroomMessageCreatedSubscription(
    `createdMessage {
      ${messageFragment}
      }
      chatroom {
        _id
        seen {
          id
          from
          lastseen
        }
        lastmessage {
          text
          messageData {
            ${messageFragment}
          }
          created {
            time
          }
        }
      }
      `,
    {
      variables: { chatroomId: chatroomId as string },
      onSubscriptionData: ({ subscriptionData }) => {
        if (
          subscriptionData &&
          subscriptionData.data &&
          subscriptionData.data.chatroomMessageCreated &&
          subscriptionData.data.chatroomMessageCreated.createdMessage
        ) {
          setMessages([
            ...[subscriptionData.data.chatroomMessageCreated.createdMessage],
            ...messages,
          ]);
          const messageContainer = document.getElementById('message-container');

          if (messageContainer) {
            messageContainer.scrollTo({
              top: messageContainer.scrollHeight,
            });
          }
        }
      },
    }
  );

  async function getMessageData(isNewChatroom?: boolean) {
    const response = await client.query({
      query: messageQuery(messageFragment, undefined, true),
      variables: {
        filter: { roomId: chatroomId },
        sort: { createdTime: -1 },
        limit,
        skip: offset,
      },
    });
    if (
      response &&
      response.data.messages &&
      response.data.messages.items &&
      response.data.messages.items.length
    ) {
      if (isNewChatroom) {
        setMessages(response.data.messages.items);
      } else {
        setMessages([...messages, ...response.data.messages.items]);
      }
      setMessagePagination(response.data.messages.pagination);
    }
  }

  useEffect(() => {
    getMessageData();
  }, [offset]);
  useEffect(() => {
    setMessages([]);
    if (chatroomId) {
      getMessageData(true);
    }
  }, [chatroomId]);

  if (loading && chatroomId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  if (chatroomId && !chatroom) {
    return <NotFound />;
  }

  function onClickSendMessage() {
    if (message.trim()) {
      createMessageMutation({
        variables: {
          chatroomId,
          companyId: contextCompany._id,
          text: message,
          tags: messageTags,
        },
      });
    }
  }

  return (
    <Grid container className={classes.root}>
      <Grid container item xs={12}>
        {chatroom && chatroom.users && (
          <Grid
            container
            item
            xs={12}
            spacing={1}
            className={classes.avatarContainer}
            alignItems="center"
          >
            <Grid item>
              <Typography variant="body1">
                {t('user', { count: 2 })}:
              </Typography>
            </Grid>
            <Grid item>
              <AvatarGroup max={6}>
                {chatroom.users.map((user) => (
                  <React.Fragment key={user.id}>
                    {user.userData && user.companyData ? (
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        badgeContent={
                          <SchemaCompanyLogoAvatar
                            useIndexLink
                            badge
                            company={user.companyData}
                          />
                        }
                      >
                        <SchemaUserAvatar useIndexLink user={user.userData} />
                      </Badge>
                    ) : (
                      <>
                        {user.userData && (
                          <SchemaUserAvatar useIndexLink user={user.userData} />
                        )}
                        {user.companyData && (
                          <SchemaCompanyLogoAvatar
                            useIndexLink
                            company={user.companyData}
                          />
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </AvatarGroup>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        direction="column-reverse"
        className={classes.messageContainer}
        id="message-container"
      >
        {messages.map((tempMessage) => (
          <Message key={tempMessage._id} message={tempMessage} />
        ))}
        {!loading && messagePagination && messagePagination.hasNextPage && (
          <Grid item xs={12} container justifyContent="center">
            <ButtonSubmit
              variant="outlined"
              color="primary"
              onClick={() => {
                setOffset(offset + limit);
              }}
            >
              {t('load_more')}
            </ButtonSubmit>
          </Grid>
        )}
      </Grid>
      {chatroom &&
        isSuperAdmin &&
        !chatroom.users.find(
          (user) =>
            user.id === contextUser._id ||
            contextUser.companies.find((c) => c.id === user.from)
        ) && (
          <Grid item xs={12}>
            <Button variant="contained" fullWidth color="primary" size="large">
              {t('you_are_viewing_this_chatroom_as_master_account')}
            </Button>
          </Grid>
        )}
      <Grid item xs={12} className={classes.inputContainer}>
        <TextField
          multiline
          className={classes.textfieldMessage}
          fullWidth
          variant="filled"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              onClickSendMessage();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={() => onClickSendMessage()}
                >
                  {t('send')}
                </Button>
              </InputAdornment>
            ),
          }}
          placeholder={t('type_your_message_here')}
        />
      </Grid>
    </Grid>
  );
}
